import { FC, useMemo, useState } from "react"
import classNames from "classnames"
import { useDispatch, useSelector } from "react-redux"
import Actions from "./Actions"
import Cell from "./Cell"
import TextField from "./TextField"
import Spinner, { SpinnerSize } from "../shared/Spinner"
import { Customer } from "."
import { fieldConfig } from "@/pages/ControlPanel/config"
import { AppDispatch, RootState } from "@/store"
import { updateCustomer } from "@/store/customersSlice"
import { CustomerForm } from "@/types/customer"
import { convertCamelToKebab, getErrorMessage } from "@/utils/index"

type CustomerItemData = Omit<Customer, "id" | "state" | "facilities">

export const SingleCustomer: FC<{
   id: string
   customerItem: CustomerItemData
   isDisabled: boolean
   editModeActiveRow: string
   deleteActiveRow?: string
   onEditModeChange: (id: string) => void
   onCreateFacility: () => void
   onDelete: () => void
}> = ({
   id,
   customerItem,
   isDisabled,
   editModeActiveRow,
   deleteActiveRow = "",
   onEditModeChange,
   onCreateFacility,
   onDelete,
}) => {
   const dispatch = useDispatch<AppDispatch>()
   const { loadingCustomer, errorCustomer } = useSelector(
      ({ customersReducer }: RootState) => customersReducer
   )
   const { loadingFacility, errorFacility } = useSelector(
      ({ facilitiesReducer }: RootState) => facilitiesReducer
   )
   const { customerName } = customerItem
   const [customerItemDataForm, setCustomerName] =
      useState<CustomerItemData["customerName"]>(customerName)
   const isActiveRow = useMemo(() => {
      return editModeActiveRow === id || deleteActiveRow === id
   }, [editModeActiveRow, deleteActiveRow, id])

   const isLoading = useMemo(() => {
      return loadingCustomer || loadingFacility
   }, [loadingCustomer, loadingFacility])

   const isEditMode = useMemo(() => {
      return editModeActiveRow === id
   }, [editModeActiveRow, id])

   const customerNameValue =
      customerItemDataForm !== undefined ? customerItemDataForm : customerName
   const isCustomerNameEmpty =
      !customerNameValue || customerNameValue.trim().length === 0
   const isFormValid = !isCustomerNameEmpty && deleteActiveRow !== id

   const handleSave = () => {
      const customerDataToUpdate: CustomerForm = {
         name: customerItemDataForm,
      }

      if (isFormValid) {
         dispatch(
            updateCustomer({
               customerId: id,
               customerData: customerDataToUpdate,
            })
         ).then(() => {
            onEditModeChange("")
         })
      }
   }

   return (
      <div
         className={classNames("customers-facilities-table__group--customer", {
            "customers-facilities-table__group--loading": isLoading,
            "customers-facilities-table__group--error":
               errorFacility && isActiveRow,
         })}
      >
         {isLoading && isActiveRow ? (
            <Spinner size={SpinnerSize.SMALL} />
         ) : errorCustomer && isActiveRow ? (
            getErrorMessage(errorCustomer)
         ) : (
            <>
               <span className="customers-facilities-table__customer-info">
                  {Object.entries(customerItem).map(([key, value], index) => {
                     return (
                        <span
                           key={`${key}-${index}`}
                           className={`customers-facilities-table__${convertCamelToKebab(key)}`}
                        >
                           {key === "customerName" ? (
                              <Cell
                                 name={key}
                                 className="customers-facilities-table__input--small"
                                 value={
                                    customerItemDataForm !== undefined
                                       ? customerItemDataForm
                                       : customerName
                                 }
                                 isEditMode={isEditMode}
                                 handleCellChange={(e) => {
                                    setCustomerName(e.target.value)
                                 }}
                                 placeholder={fieldConfig[key]?.placeholder}
                              >
                                 {value || "-"}
                              </Cell>
                           ) : (
                              <TextField name={key}>{value}</TextField>
                           )}
                        </span>
                     )
                  })}
               </span>
               <Actions
                  isDisabled={isDisabled}
                  isEditMode={isEditMode}
                  isFormValid={isFormValid}
                  showAddUser={false}
                  onDelete={() => onDelete()}
                  onSave={handleSave}
                  addFacility={onCreateFacility}
                  addUser={() => console.log("Add User")}
                  cancel={() => onEditModeChange("")}
                  onEditMode={() => onEditModeChange(id)}
               />
            </>
         )}
      </div>
   )
}
